import React from "react";
import styled from "styled-components";

import * as u from "../../utils";
import * as T from "../../components/Typography";
import * as UserContext from "../../components/UserContext";
import firebase from "../../firebase.client";
import LessonCard from "../../components/AcademyLessonCard";
import RxSubNavAcademy from "../../components/ui/navigation/RxSubNavAcademy";
import UpsellModal from "../../components/UpsellModal";

export default function Academy(props) {
  const userInfo = React.useContext(UserContext.Context);
  const [startedCourses, setStartedCourses] = React.useState([]);
  const [completedCourses, setCompletedCourses] = React.useState([]);
  const [savedCourseIds, setSavedCourseIds] = React.useState([]);
  const [modalType, setModalType] = React.useState("track");

  React.useEffect(() => {
    if (userInfo.id) setModalType(false);
  }, [userInfo.id]);
  React.useEffect(() => {
    getMyCourses();
  }, [userInfo]);

  return (
    <>
      <RxSubNavAcademy />
      <S.Container>
        <UpsellModal type={modalType} setType={setModalType} />
        <S.MyCourses>{getMyLearning()}</S.MyCourses>
      </S.Container>
    </>
  );

  async function getMyCourses() {
    if (!userInfo.id) return;
    const data = (
      await firebase.firestore().collection("users").doc(userInfo.id).get()
    ).data();

    if (data?.savedCourses) setSavedCourseIds(data.savedCourses);
    if (data?.courses?.length) parseAndSetMyCourses(data.courses);

    function parseAndSetMyCourses(myCourses) {
      let completedCourses = [];
      let startedCourses = [];

      props.lessons.forEach((lesson) => {
        const myStartedCourse = myCourses.find(
          (course) => course.id === lesson.sys.id
        );
        if (!myStartedCourse) return;
        const lessonLength = lesson.lessonActivitiesCollection.total;
        const completedLessonLength = myStartedCourse.completedLessons.length;
        const progress = completedLessonLength / lessonLength;
        lesson.progress = progress;
        if (progress === 1) completedCourses.push(lesson);
        else startedCourses.push(lesson);
      });

      setCompletedCourses(completedCourses);
      setStartedCourses(startedCourses);
    }
  }

  function getMyLearning() {
    const hasStartedCourses = !!startedCourses.length;
    const hasCompletedCourses = !!completedCourses.length;
    const hasSavedCourses = !!savedCourseIds.length;

    return (
      <>
        <CoursesSection
          title="Pick up where you left off"
          hasLessons={hasStartedCourses}
          lessons={startedCourses.map(getLesson)}
          noLessonsText="You have no courses currently in progress. Why not start a new one now?"
        />
        <CoursesSection
          title="Saved courses"
          hasLessons={hasSavedCourses}
          lessons={props.lessons.filter(bySaved).map(getLesson)}
          noLessonsText="You do not have any saved courses. Click the bookmark icon on a course to save it here!"
        />
        <CoursesSection
          title="Completed courses"
          hasLessons={hasCompletedCourses}
          lessons={completedCourses.map(getLesson)}
          noLessonsText="You haven't finished any courses yet. Keep going!"
        />
      </>
    );

    function CoursesSection(props) {
      return (
        <>
          <T.H3>{props.title}</T.H3>
          {props.hasLessons ? (
            <S.Lessons>{props.lessons}</S.Lessons>
          ) : (
            <T.CM>{props.noLessonsText}</T.CM>
          )}
          <S.Line />
        </>
      );
    }

    function bySaved(lesson) {
      return savedCourseIds.includes(lesson.sys.id);
    }
  }

  function getLesson(lesson) {
    return (
      <LessonCard
        key={lesson.sys.id}
        savedCourseIds={savedCourseIds}
        setSavedCourseIds={setSavedCourseIds}
        {...lesson}
      />
    );
  }
}

export async function getStaticProps({ locale }) {
  // wasn't sure if Region needed to be converted to locale, or how else to do this
  let localeRegion = locale.split("en-")[1].toUpperCase();
  if (localeRegion === "AU") localeRegion += "S";
  if (localeRegion === "GB") localeRegion = "UK";
  if (localeRegion === "IN") localeRegion = "UK";

  const axios = require("axios");
  const config = require("../../config");

  const lessonData = (
    await axios.post(config.CONTENTFUL_GENERAL, getLessonsQuery())
  ).data.data;
  const lessons = lessonData.lessonCollection.items.filter((item) =>
    item.locale ? item.locale.includes(locale) : true
  );
  const courseTags = lessonData.tagCollection.items;
  const categoryTags = lessonData.categoryTagCollection.items;

  const collectionData = (
    await axios.post(config.CONTENTFUL_GENERAL, getCollectionsQuery())
  ).data.data;
  const collectionsNoGoals = collectionData.courseCollection.items.filter(
    (item) => {
      return item.region?.length ? item.region.includes(localeRegion) : true;
    }
  );

  // add associated goals to collections for future use in suggesting content
  const goalsData = (
    await axios.post(config.CONTENTFUL_GENERAL, getGoalsQuery())
  ).data.data;
  const goals = goalsData.goalCollection.items;
  const collections = collectionsNoGoals.map((coll) => {
    const [thisCollection] = goalsData.courseCollection.items.filter(
      (c) => coll.courseName === c.courseName
    );
    const goals = thisCollection.goalTagsCollection.items.map((g) => g.name);
    return { ...coll, goals };
  });

  return {
    props: { lessons, courseTags, categoryTags, collections, goals },
    revalidate: 3600,
  };

  function getLessonsQuery() {
    return {
      query: `{
        categoryTagCollection {
          items { name }
        }

        tagCollection {
          items { name }
        }

        lessonCollection {
          items {
            locale
            lessonName
            courseTags
            categoryTags
            urlSlug
            lessonIntroductionText
            featuredCourseMobileImage { url title }
            featuredCourseImage { url title}
            featuredCourseDescription
            coverImage { title url }
            sys { id publishedAt }
            lessonActivitiesCollection { total }
          }
        }
      }`,
    };
  }

  function getCollectionsQuery() {
    return {
      query: `{
        courseCollection {
          items {
            courseName
            courseCardImage { title url }
            urlSlug
            region
            coursesCollection { total }
          }
        }
      }`,
    };
  }

  function getGoalsQuery() {
    return {
      query: `{
        goalCollection {
          items { name }
        }

        courseCollection {
          items {
            courseName
            goalTagsCollection { items { name } }
          }
        }
      }`,
    };
  }
}

const S = {};

S.Container = styled.div`
  max-width: 1440px;
  margin: auto;
  padding: 20px 16px;

  @media ${u.device.tablet} {
    padding: 20px;
  }

  @media ${u.device.desktop} {
    padding: 40px;
  }
`;

S.MyCourses = styled.div`
  margin-bottom: 30px;
`;

S.Lessons = styled.div`
  margin: 15px 0 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 25px;
  @media ${u.device.tablet} {
    justify-content: space-between;
  }
`;

S.Line = styled.div`
  width: 100%;
  height: 2px;
  background: ${u.colours.shellOverlay};
  margin: 20px 0;
`;
